import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArtistProfileForm } from '@/components/ArtistProfileForm';
import { ShowDetailsForm } from '@/components/ShowDetailsForm';
import { type Person, type Happening } from '@littlebooker/dal';
import { apiGetPublic } from '@/lib/api';
import {
    missingArtistFieldsOnPerson,
    missingShowFieldsOnHappening,
    sameStatus,
    type InviteFormHandle,
    type InviteFormStatus,
} from '@/components/inviteRequirements';

type BannerVariant = 'info' | 'success' | 'warning' | 'error';

type StatusBannerProps = {
    icon?: ReactNode;
    title: string;
    body: string;
    variant: BannerVariant;
    action?: ReactNode;
};

const bannerStyles: Record<BannerVariant, string> = {
    info: 'bg-blue-50 text-blue-900 border-blue-100',
    success: 'bg-emerald-50 text-emerald-900 border-emerald-100',
    warning: 'bg-amber-50 text-amber-900 border-amber-100',
    error: 'bg-red-50 text-red-900 border-red-100',
};

function StatusBanner({ icon, title, body, variant, action }: StatusBannerProps) {
    return (
        <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${bannerStyles[variant]}`}>
            <div className="mt-0.5 text-lg">{icon}</div>
            <div className="flex-1">
                <p className="font-semibold">{title}</p>
                <p className="text-sm/relaxed opacity-90">{body}</p>
            </div>
            {action}
        </div>
    );
}

function StepIndicator({ step, label, done }: { step: number; label: string; done: boolean }) {
    return (
        <div className="flex items-center gap-2">
            <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                    done
                        ? 'bg-emerald-500 text-white'
                        : 'border-2 border-primary bg-background text-primary'
                }`}
            >
                {done ? <CheckCircle2 className="h-4 w-4" /> : step}
            </div>
            <span className={`text-sm font-medium ${done ? 'text-emerald-700' : 'text-foreground'}`}>
                {label}
            </span>
        </div>
    );
}

export default function CombinedInvitePage() {
    const { personSlug, showSlug } = useParams<{ personSlug: string; showSlug: string }>();
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get('invite') ?? '';
    const stubToken = searchParams.get('stub') ?? '';
    const queryClient = useQueryClient();

    const [profileSaved, setProfileSaved] = useState(false);
    const [showSaved, setShowSaved] = useState(false);
    // Sections still missing required fields at the last submit attempt, so the page
    // can say exactly what is blocking rather than just refusing.
    const [blockedMissing, setBlockedMissing] = useState<{ profile: string[]; show: string[] } | null>(null);
    const profileSectionRef = useRef<HTMLDivElement>(null);
    const showDetailsRef = useRef<HTMLDivElement>(null);
    const profileFormRef = useRef<InviteFormHandle>(null);
    const showFormRef = useRef<InviteFormHandle>(null);
    // Live status from each form, so the shared bar can nudge before anyone submits.
    const [profileStatus, setProfileStatus] = useState<InviteFormStatus>({ missing: [], dirty: false });
    const [showStatus, setShowStatus] = useState<InviteFormStatus>({ missing: [], dirty: false });
    const handleProfileStatus = useCallback(
        (next: InviteFormStatus) => setProfileStatus((prev) => (sameStatus(prev, next) ? prev : next)),
        [],
    );
    const handleShowStatus = useCallback(
        (next: InviteFormStatus) => setShowStatus((prev) => (sameStatus(prev, next) ? prev : next)),
        [],
    );

    // Load person
    const {
        data: person,
        isLoading: personLoading,
        isError: personIsError,
        error: personError,
    } = useQuery({
        // Fetch by token via the public API (server validates + bypasses RLS).
        queryKey: ['public-artist', inviteToken],
        queryFn: () => apiGetPublic<Person>('/artist', { token: inviteToken }),
        enabled: Boolean(inviteToken),
    });

    // Load happening
    const {
        data: happening,
        isLoading: happeningLoading,
        isError: happeningIsError,
        error: happeningError,
    } = useQuery({
        queryKey: ['public-show', stubToken],
        queryFn: () => apiGetPublic<Happening>('/show', { token: stubToken }),
        enabled: Boolean(stubToken),
    });

    const inviteIsValid = useMemo(() => {
        if (!person || !inviteToken) return false;
        return Boolean(person.invite_token && person.invite_token === inviteToken);
    }, [inviteToken, person]);

    const stubIsValid = useMemo(() => {
        if (!happening || !stubToken) return false;
        return Boolean(happening.metadata?.stub_token && happening.metadata.stub_token === stubToken);
    }, [stubToken, happening]);

    const isLoading = personLoading || happeningLoading;

    const handleProfileSaved = useCallback(() => {
        setProfileSaved(true);
        // Step 1 is usually where the artist photo gets uploaded, and the show's
        // metadata.artists (rebuilt server-side from people.image_url) is what the
        // "use an artist photo" shortcut reads. Without this refetch the show step
        // still holds the pre-upload snapshot and the shortcut never appears —
        // exactly when it is most useful, since the show photo is now required.
        queryClient.invalidateQueries({ queryKey: ['public-show', stubToken] });
    }, [queryClient, stubToken]);

    const handleShowSaved = useCallback(() => {
        setShowSaved(true);
    }, []);

    /**
     * Single submit for both halves. An artist seeing a combined invite is being asked
     * for a profile *and* a show, and we kept getting one without the other — so this
     * refuses to report success until every required field on both forms is filled.
     *
     * A half that does validate is still saved: typed work (a long bio especially) is
     * not worth losing, and saving the profile is also what makes the show section's
     * "reuse an artist photo" shortcut appear, which is often the very thing needed to
     * clear the remaining error.
     */
    const handleSubmitAll = useCallback(() => {
        const profileMissing = profileFormRef.current?.validate() ?? [];
        const showMissing = showFormRef.current?.validate() ?? [];

        if (profileMissing.length === 0) profileFormRef.current?.save();
        if (showMissing.length === 0) showFormRef.current?.save();

        if (profileMissing.length === 0 && showMissing.length === 0) {
            setBlockedMissing(null);
            return;
        }

        setBlockedMissing({ profile: profileMissing, show: showMissing });
        const target = profileMissing.length > 0 ? profileSectionRef : showDetailsRef;
        target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    // Render error/loading for missing params
    if (!personSlug || !showSlug) {
        return (
            <PageShell>
                <StatusBanner
                    variant="error"
                    title="Incomplete link"
                    body="This link is missing required information. Double-check the link you were sent."
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            </PageShell>
        );
    }

    if (!inviteToken || !stubToken) {
        return (
            <PageShell>
                <StatusBanner
                    variant="warning"
                    title="Missing tokens"
                    body="This link is missing required tokens. Ask your LittleBooker admin to resend the invite."
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            </PageShell>
        );
    }

    if (isLoading) {
        return (
            <PageShell>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading your invite…
                </div>
            </PageShell>
        );
    }

    if (personIsError || happeningIsError) {
        const msg = personIsError
            ? (personError instanceof Error ? personError.message : 'Could not load your profile.')
            : (happeningError instanceof Error ? happeningError.message : 'Could not load your show.');
        return (
            <PageShell>
                <StatusBanner
                    variant="error"
                    title="Something went wrong"
                    body={msg}
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            </PageShell>
        );
    }

    if (!person || !happening) {
        return (
            <PageShell>
                <StatusBanner
                    variant="error"
                    title="Not found"
                    body="We couldn't find the artist or show linked in this invite. Ask your admin to double-check and resend."
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            </PageShell>
        );
    }

    // Validate tokens
    if (!inviteIsValid || !stubIsValid) {
        return (
            <PageShell>
                <StatusBanner
                    variant="error"
                    title="Invite expired or invalid"
                    body="One or both tokens in this link don't match what we have on file. Request a fresh link to continue."
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            </PageShell>
        );
    }

    // Saved in this visit, or already complete when the page loaded — so a returning
    // artist who finished last week isn't shown two empty checkboxes.
    const profileComplete = profileSaved || missingArtistFieldsOnPerson(person).length === 0;
    const showComplete = showSaved || missingShowFieldsOnHappening(happening).length === 0;
    const allDone = profileComplete && showComplete;

    // What the shared save bar reacts to. Live, not just after a submit attempt: an
    // artist who fills half the page and wanders off is exactly the case that kept
    // producing half-finished shows, so the bar stays red and names what is left.
    const liveMissing = [...profileStatus.missing, ...showStatus.missing];
    const anythingDirty = profileStatus.dirty || showStatus.dirty;
    const needsAttention = liveMissing.length > 0 || anythingDirty;

    return (
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/50">
            <div className="mx-auto flex min-h-screen max-w-3xl flex-col gap-6 p-6 py-12">
                <div className="text-center">
                    <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                        LittleBooker · Artist Portal
                    </p>
                    <h1 className="mt-2 text-3xl font-bold tracking-tight">
                        Complete your profile & show details
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Fill in both sections below so the programming team has everything they need.
                    </p>
                </div>

                {/* Progress stepper */}
                <div className="flex items-center justify-center gap-6 rounded-lg border bg-background px-6 py-4 shadow-sm">
                    <StepIndicator step={1} label="Your Profile" done={profileComplete} />
                    <div className="h-px w-8 bg-border" />
                    <StepIndicator step={2} label="Show Details" done={showComplete} />
                </div>

                {allDone && (
                    <StatusBanner
                        variant="success"
                        title="All done!"
                        body="Both your profile and show details have been saved. You can revisit this page any time to make updates."
                        icon={<CheckCircle2 className="h-5 w-5" />}
                    />
                )}

                {/* What's still blocking the submit, named per section */}
                {blockedMissing && (
                    <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                            <div className="flex-1 space-y-2">
                                <p className="font-semibold">Not submitted yet — a few fields are still missing</p>
                                <p className="text-sm/relaxed opacity-90">
                                    Both your profile and your show details are needed before this counts as
                                    complete. Anything you already filled in has been saved.
                                </p>
                                {blockedMissing.profile.length > 0 && (
                                    <MissingList
                                        heading="Your Profile"
                                        fields={blockedMissing.profile}
                                        onJump={() => profileSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                    />
                                )}
                                {blockedMissing.show.length > 0 && (
                                    <MissingList
                                        heading="Show Details"
                                        fields={blockedMissing.show}
                                        onJump={() => showDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Section 1: Profile */}
                <div ref={profileSectionRef}>
                <Card className={`border-primary/10 shadow-xl ${profileComplete ? 'opacity-75' : ''}`}>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-2xl">
                            {profileComplete && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                            Your Profile
                        </CardTitle>
                        <CardDescription>
                            Fill in the essentials so your host can publish your profile.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <ArtistProfileForm
                            person={person}
                            inviteToken={inviteToken}
                            onSaveSuccess={handleProfileSaved}
                            hideSubmitButton
                            controlRef={profileFormRef}
                            onStatusChange={handleProfileStatus}
                        />
                    </CardContent>
                </Card>
                </div>

                {/* Section 2: Show Details */}
                <div ref={showDetailsRef}>
                    <Card className={`border-primary/10 shadow-xl ${showComplete ? 'opacity-75' : ''}`}>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-2xl">
                                {showComplete && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                                Show Details
                            </CardTitle>
                            <CardDescription>
                                Tell us about your upcoming set - description, genres, equipment needs.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <ShowDetailsForm
                                happening={happening}
                                stubToken={stubToken}
                                onSaveSuccess={handleShowSaved}
                                hideSubmitButton
                                controlRef={showFormRef}
                                onStatusChange={handleShowStatus}
                            />

                        </CardContent>
                    </Card>
                </div>

                {/* One submit for both halves — see handleSubmitAll */}
                <div
                    className={`sticky bottom-0 z-10 -mx-6 border-t px-6 py-4 transition-colors duration-300 ${
                        needsAttention
                            ? 'border-destructive/30 bg-destructive/10'
                            : 'border-border bg-background/95 backdrop-blur-sm'
                    }`}
                >
                    <div className="flex flex-col items-center gap-2">
                        <Button
                            type="button"
                            onClick={handleSubmitAll}
                            size="lg"
                            className={`w-full md:w-auto ${
                                needsAttention ? 'animate-nudge bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''
                            }`}
                        >
                            {allDone && !anythingDirty ? 'Save changes' : 'Submit profile & show details'}
                        </Button>
                        {liveMissing.length > 0 ? (
                            <p className="text-center text-sm font-medium text-destructive">
                                Still to fill in: {liveMissing.join(', ')}
                            </p>
                        ) : anythingDirty ? (
                            <p className="text-sm font-medium text-destructive">
                                You have unsaved changes — nothing is submitted until you press the button.
                            </p>
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                Both sections are required before this is marked complete.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/** One section's outstanding required fields, with a jump link to that section. */
function MissingList({ heading, fields, onJump }: { heading: string; fields: string[]; onJump: () => void }) {
    return (
        <div>
            <button
                type="button"
                onClick={onJump}
                className="text-sm font-semibold underline underline-offset-2 hover:no-underline"
            >
                {heading} <ChevronDown className="inline h-3.5 w-3.5" />
            </button>
            <ul className="mt-1 list-disc pl-5">
                {fields.map((field) => (
                    <li key={field}>{field}</li>
                ))}
            </ul>
        </div>
    );
}

function PageShell({ children }: { children: ReactNode }) {
    return (
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/50">
            <div className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center gap-6 p-6">
                <div className="text-center">
                    <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                        LittleBooker · Artist Portal
                    </p>
                    <h1 className="mt-2 text-3xl font-bold tracking-tight">
                        Complete your profile & show details
                    </h1>
                </div>
                <Card className="border-primary/10 shadow-xl">
                    <CardContent className="p-6">{children}</CardContent>
                </Card>
            </div>
        </div>
    );
}
